import Dagre from '@dagrejs/dagre';
import {
	queryKeyFn,
	type LineageFlowChild,
	type LineageFlowImpactedChildren,
	type QueryOperation,
} from '@repo/api-codegen';
import { EntityType } from '@repo/common/enums/entityType';
// eslint-disable-next-line no-restricted-imports
import { getColor } from '@repo/theme/utils';
import type { XYPosition } from '@xyflow/react';
import { MarkerType } from '@xyflow/react';
import { includes } from 'lodash-es';
import type { LineageFlowEdge } from '../components/EntityEdge/EntityEdge.types';
import { ENTITY_NODE_WIDTH } from '../components/EntityNode/EntityNode.constants';
import {
	isLineageFlowNode,
	LineageFlowNodeVisibilityState,
	type LineageFlowNode,
} from '../components/EntityNode/EntityNode.types';
import {
	ENTITY_CHILDREN_CONTROL_HEIGHT,
	ENTITY_CHILDREN_MAX_IN_VIEW,
	ENTITY_CHILDREN_ROW_HEIGHT,
	ENTITY_CHILDREN_SEARCH_HEIGHT,
} from '../components/EntityNodeChildren/EntityNodeChildren.constants';
import { ENTITY_NODE_HEADER_HEIGHT } from '../components/EntityNodeHeader/EntityNodeHeader.constants';
import {
	DAGRE_EDGE_SEP,
	DAGRE_NODE_SEP,
	DAGRE_RANK_SEP,
} from '../components/LineageFlow/LineageFlow.constants';

import type { QueryClient } from '@tanstack/react-query';
import * as Comlink from 'comlink';
import { TEMPORARY_NODE_HEIGHT } from '../components/TemporaryNode/TemporaryNode.constants';
import {
	isLineageFlowTemporaryNode,
	type LineageFlowTemporaryNode,
} from '../components/TemporaryNode/TemporaryNode.types';

const isValidMainEdge = (edge: LineageFlowEdge): boolean =>
	edge.source !== edge.target &&
	includes(edge.sourceHandle, 'MAIN_SOURCE') &&
	includes(edge.targetHandle, 'MAIN_TARGET');

export const isNodeBelow = ({
	positionA,
	positionB,
}: {
	positionA: XYPosition;
	positionB: XYPosition;
}): boolean => positionA.y > positionB.y;

export const calculateEntityNodeChildrenHeight = ({
	count,
	expanded = true,
}: {
	count: number;
	expanded?: boolean;
}): number => {
	if (!expanded) {
		return 0;
	}

	return (
		ENTITY_CHILDREN_SEARCH_HEIGHT +
		Math.min(count, ENTITY_CHILDREN_MAX_IN_VIEW) * ENTITY_CHILDREN_ROW_HEIGHT
	);
};

export const calculateEntityNodeHighlightedChildrenHeight = ({
	count,
}: {
	count: number;
}): number =>
	ENTITY_CHILDREN_SEARCH_HEIGHT + count * ENTITY_CHILDREN_ROW_HEIGHT;

export const calculateEntityNodeHeight = (
	node: LineageFlowNode | LineageFlowTemporaryNode,
	highlightedChildren?: LineageFlowChild[]
): number => {
	if (!isLineageFlowNode(node)) {
		return TEMPORARY_NODE_HEIGHT;
	}

	const { entity_type, children_count, show_children } = node.data;

	if (entity_type === EntityType.table && highlightedChildren) {
		return (
			ENTITY_NODE_HEADER_HEIGHT +
			ENTITY_CHILDREN_CONTROL_HEIGHT +
			calculateEntityNodeHighlightedChildrenHeight({
				count: highlightedChildren.length,
			})
		);
	}

	if (
		entity_type === EntityType.table &&
		children_count &&
		children_count > 0
	) {
		return (
			ENTITY_NODE_HEADER_HEIGHT +
			ENTITY_CHILDREN_CONTROL_HEIGHT +
			calculateEntityNodeChildrenHeight({
				count: children_count,
				expanded: show_children,
			})
		);
	}

	if (node.data.visibility === LineageFlowNodeVisibilityState.LOADING) {
		return ENTITY_NODE_HEADER_HEIGHT + ENTITY_CHILDREN_CONTROL_HEIGHT;
	}

	return ENTITY_NODE_HEADER_HEIGHT;
};

// Layout worker - initialized lazily
let layoutWorker: Worker | undefined;
let layoutWorkerProxy:
	| ((args: {
			nodeDimensions: Array<{ id: string; width: number; height: number }>;
			edges: Array<{
				source: string;
				target: string;
				sourceHandle?: string;
				targetHandle?: string;
			}>;
	  }) => Promise<{
			nodePositions: Array<{
				id: string;
				x: number;
				y: number;
				rank?: number;
				finalX: number;
				finalY: number;
			}>;
			rankMap: Array<[number, string[]]>;
	  }>)
	| undefined;

const getLayoutWorker = () => {
	if (typeof window === 'undefined') {
		return undefined;
	}
	if (!layoutWorker) {
		layoutWorker = new Worker(
			new URL('../worker/layout.worker.ts', import.meta.url),
			{ type: 'module' }
		);
		layoutWorkerProxy = Comlink.wrap(
			layoutWorker
		) as unknown as typeof layoutWorkerProxy;
	}
	return layoutWorkerProxy;
};

export const calculateLayout = async ({
	nodes,
	edges,
	fetchedID,
	fetchedDirection,
	highlightedParentChildMap,
}: {
	nodes: (LineageFlowNode | LineageFlowTemporaryNode)[];
	edges: LineageFlowEdge[];
	fetchedID?: string;
	fetchedDirection?: 'UPSTREAM' | 'DOWNSTREAM';
	highlightedParentChildMap?: Map<string, LineageFlowChild[]>;
}): Promise<{
	updatedGraph: Dagre.graphlib.Graph;
	updatedRankMap: Map<number, string[]>;
	updatedNodes: (LineageFlowNode | LineageFlowTemporaryNode)[];
}> => {
	// Calculate node dimensions on main thread
	const nodeDimensions = nodes.map((node) => ({
		id: node.id,
		width: node.measured?.width ?? ENTITY_NODE_WIDTH,
		height: calculateEntityNodeHeight(
			node,
			highlightedParentChildMap?.get(node.id)
		),
		isTemporary: isLineageFlowTemporaryNode(node),
	}));

	// Call worker for expensive Dagre layout calculation
	const worker = getLayoutWorker();
	if (!worker) {
		throw new Error('Layout worker not available');
	}

	const { nodePositions, rankMap: rankMapArray } = await worker({
		nodeDimensions,
		edges: edges.map((edge) => {
			const result: {
				source: string;
				target: string;
				sourceHandle?: string;
				targetHandle?: string;
			} = {
				source: edge.source,
				target: edge.target,
			};
			if (edge.sourceHandle !== null) {
				result.sourceHandle = edge.sourceHandle;
			}
			if (edge.targetHandle !== null) {
				result.targetHandle = edge.targetHandle;
			}
			return result;
		}),
	});

	// Reconstruct graph on main thread
	const updatedGraph = new Dagre.graphlib.Graph()
		.setDefaultEdgeLabel(() => ({}))
		.setDefaultNodeLabel(() => ({}));

	updatedGraph.setGraph({
		rankdir: 'LR',
		nodesep: DAGRE_NODE_SEP,
		edgesep: DAGRE_EDGE_SEP,
		ranksep: DAGRE_RANK_SEP,
	});

	nodeDimensions.forEach(({ id, width, height }) => {
		updatedGraph.setNode(id, { width, height });
	});

	edges.forEach((edge) => {
		if (isValidMainEdge(edge)) {
			updatedGraph.setEdge(edge.source, edge.target);
		}
	});

	// Set positions from worker results (using raw x, y for graph)
	nodePositions.forEach(({ id, x, y, rank }) => {
		const node = updatedGraph.node(id);
		node.x = x;
		node.y = y;
		if (rank !== undefined) {
			node.rank = rank;
		}
	});

	const updatedRankMap = new Map<number, string[]>(rankMapArray);

	// Update nodes with positions
	const updatedNodes = nodes.map((node) => {
		const position = nodePositions.find((p) => p.id === node.id);
		if (!position) {
			return node;
		}

		if (isLineageFlowTemporaryNode(node)) {
			return {
				...node,
				position: {
					x: position.finalX,
					y: position.finalY,
				},
				data: {
					...node.data,
					nodeId: node.data.nodeId,
					position: node.data.position,
				},
			} as LineageFlowTemporaryNode;
		}

		let fetched = {
			upstream_fetched: node.data.upstream_fetched,
			downstream_fetched: node.data.downstream_fetched,
		};

		if (fetchedID === node.id) {
			if (fetchedDirection === 'UPSTREAM') {
				fetched = {
					...fetched,
					upstream_fetched: true,
				};
			} else if (fetchedDirection === 'DOWNSTREAM') {
				fetched = {
					...fetched,
					downstream_fetched: true,
				};
			}
		}

		return {
			...node,
			position: {
				x: position.finalX,
				y: position.finalY,
			},
			data: {
				...node.data,
				...fetched,
			},
		} as LineageFlowNode;
	});

	return {
		updatedGraph,
		updatedRankMap,
		updatedNodes,
	};
};

const processImpactedChild = (
	child: LineageFlowChild,
	highlightedParentChildMap: Map<string, LineageFlowChild[]>,
	processedChildIDs: Set<string>,
	expandedNodes: Set<string>
) => {
	if (!processedChildIDs.has(child.id)) {
		processedChildIDs.add(child.id);
		const parentID = child.parent_id;
		highlightedParentChildMap.set(parentID, [
			...(highlightedParentChildMap.get(parentID) || []),
			child,
		]);
		expandedNodes.add(parentID);
	}
};

const createChildEdge = (
	fromChild: LineageFlowChild,
	toChild: LineageFlowChild
): LineageFlowEdge => {
	const type = toChild.entity_type === EntityType.column ? 'CHILD' : 'ENTITY';
	const source =
		fromChild.entity_type === EntityType.column
			? fromChild.parent_id
			: fromChild.id;
	const target =
		toChild.entity_type === EntityType.column ? toChild.parent_id : toChild.id;
	const sourceHandle =
		fromChild.entity_type === EntityType.column
			? `CHILD_SOURCE--${fromChild.id}`
			: 'MAIN_SOURCE';
	const targetHandle =
		toChild.entity_type === EntityType.column
			? `CHILD_TARGET--${toChild.id}`
			: 'MAIN_TARGET';

	return {
		id: `${fromChild.id}-${toChild.id}`,
		type,
		source,
		sourceHandle,
		target,
		targetHandle,
		data: {
			hovered: false,
		},
		markerEnd: {
			type: MarkerType.Arrow,
			width: 24,
			height: 24,
			color: getColor('border/emphasis/default'),
		},
		animated: true,
		deletable: false,
	};
};

export const determineParentChildRelationship = ({
	children,
}: {
	children: LineageFlowImpactedChildren[];
}): {
	newHighlightedParentChildMap: Map<string, LineageFlowChild[]>;
	newExpandedNodeIDs: Set<string>;
	newChildEdges: LineageFlowEdge[];
} => {
	const highlightedParentChildMap = new Map<string, LineageFlowChild[]>();
	const processedChildIDs = new Set<string>();
	const expandedNodeIDs = new Set<string>();
	const childEdges: LineageFlowEdge[] = [];

	children.forEach((child) => {
		processImpactedChild(
			child.from_child,
			highlightedParentChildMap,
			processedChildIDs,
			expandedNodeIDs
		);
		processImpactedChild(
			child.to_child,
			highlightedParentChildMap,
			processedChildIDs,
			expandedNodeIDs
		);
		childEdges.push(createChildEdge(child.from_child, child.to_child));
	});

	return {
		newHighlightedParentChildMap: highlightedParentChildMap,
		newExpandedNodeIDs: expandedNodeIDs,
		newChildEdges: childEdges,
	};
};

export const fetchQuery = async <T>(
	queryClient: QueryClient,
	operation: QueryOperation,
	queryFn: () => Promise<T>,
	invalidate?: boolean
) => {
	const queryKey = queryKeyFn(operation);
	if (invalidate) {
		queryClient.invalidateQueries({ queryKey });
	}
	return queryClient.fetchQuery({ queryKey, queryFn });
};
